import { Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import BeoLogo from "@assets/images/beo_logo.svg"; // make sure the extension is correct
import { BeoButton } from "@components/common/action";
import { BeoCard, BeoPanelMenu } from "@components/common/view";
import Spinner from "@components/others/Spinner";
import { useAppInitialization } from "@hooks/useAppInitialization";
import CommonHeader from "@pages/common/header/CommonHeader";
import { commonPanelMenuStyle } from "@styles/css/common/CommonStyles";
import { useMemo, useState } from "react";
import { Outlet } from "react-router-dom";
/**
 * CommonLayout Component
 *
 * Provides the main structural layout of the Beo application,
 * including a sidebar navigation panel, top header, and a main
 * content area rendered via React Router's `<Outlet />`.
 *
 * This layout supports both desktop and mobile modes:
 * - On **desktop**, the sidebar is always visible.
 * - On **mobile**, a hamburger button toggles the sidebar visibility.
 *
 * It also handles app initialization (e.g., user logout setup)
 * and ensures that menu items are dynamically rendered from configuration.
 *
 * @component
 * @example
 * ```tsx
 * import { CommonLayout } from "@/layouts/CommonLayout";
 * import { getUserMenuItems } from "@/utils/menuUtils";
 *
 * const menuItems = getUserMenuItems(navigate);
 *
 * return <CommonLayout menuItems={menuItems} />;
 * ```
 * @author rajeev.k
 * @param {Object} props - Component props.
 * @param {MenuItem[]} props.menuItems - List of navigation menu items (typically loaded dynamically).
 *
 * @returns {JSX.Element} The rendered responsive layout with header, sidebar, and main content area.
 *
 * @remarks
 * - Uses `BeoPanelMenu` for sidebar navigation.
 * - Displays a sticky `CommonHeader` at the top of the content area.
 * - Automatically renders a `<Spinner />` if `menuItems` is empty.
 * - Includes dark mode support via Tailwind `dark:` classes.
 *
 * @see BeoPanelMenu
 * @see CommonHeader
 * @see useAppInitialization
 */
var CommonLayout = function (_a) {
    var menuItems = _a.menuItems;
    var _b = useState(), expandedKeys = _b[0], setExpandedKeys = _b[1];
    /**
     * Custom hook for application initialization (fetching user data, menu, etc.).
     * @type {object}
     * @property {() => Promise<void>} handleLogout - Function to handle user logout.
     */
    var handleLogout = useAppInitialization().handleLogout;
    var _c = useState(false), isSidebarOpen = _c[0], setIsSidebarOpen = _c[1];
    var logoutPart = useMemo(function () {
        return (_jsxs("div", { className: "sticky z-50 mb-6 top-0 ", children: [isSidebarOpen ? (_jsx(_Fragment, {})) : (_jsx(BeoCard, { id: "card", className: "hidden md:block w-full sticky top-0 h-5 bg-white dark:bg-black dark:text-white text-gray-600 shadow-[0_2px_1px_-1px_var(--primary-color),0_0px_0px_0_var(--primary-color),0_1px_0px_0_var(--primary-color)] rounded-none " })), _jsx(CommonHeader, { handleLogout: handleLogout })] }));
    }, [handleLogout, isSidebarOpen]);
    if (menuItems.length === 0) {
        return _jsx(Spinner, {});
    }
    return (_jsx(_Fragment, { children: _jsxs("div", { className: "min-h-screen flex flex-col md:flex-row bg-gray-50 dark:bg-black transition-colors ", children: [_jsxs("div", { className: "flex items-center justify-center p-3 md:hidden bg-white dark:bg-neutral-900 shadow-sm sticky top-0 z-30", children: [_jsx(BeoButton, { id: "mobile_btn", icon: "pi pi-bars", rounded: true, text: true, className: "absolute left-3 text-gray-700 dark:text-gray-200", onClick: function () { return setIsSidebarOpen(!isSidebarOpen); }, "aria-label": "Toggle menu" }), _jsx("img", { src: BeoLogo, alt: "Logo", className: "h-6 dark:bg-white dark:p-[2px] dark:rounded mx-auto" })] }), _jsxs("aside", { className: "fixed top-0 left-0 h-screen z-40 bg-gray-100 dark:bg-neutral-950 \n          p-[22px_14px] shadow-[var(--soft-shadow)] flex flex-col gap-[18px] w-[230px]\n          transform transition-transform duration-300 ease-in-out\n          ".concat(isSidebarOpen ? "translate-x-0" : "-translate-x-full", " \n          md:translate-x-0 mb-2"), children: [_jsx("img", { src: BeoLogo, alt: "Logo", className: "hidden md:block animate-zoom-in duration-500 h-8 m-8 scale-150 dark:scale-175 dark:bg-white dark:p-1 dark:rounded-md z-30" }), _jsx(BeoPanelMenu, { id: "panelmenu_user_layout", model: menuItems, multiple: true, expandedKeys: expandedKeys, onExpandedKeysChange: function (e) { return setExpandedKeys(e.value); }, className: commonPanelMenuStyle + " overflow-y-auto" })] }), _jsxs("main", { className: "flex-1 px-[15px] py-[4px] transition-all duration-300 dark:bg-black\n          md:ml-[230px]", children: [logoutPart, _jsx(Outlet, {})] }), isSidebarOpen && (_jsx("div", { className: "fixed inset-0 bg-black/40 z-30 md:hidden", onClick: function () { return setIsSidebarOpen(false); } }))] }) }));
};
export default CommonLayout;
